import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Tooltip,
  Modal,
  message,
  Pagination,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ArrowLeftOutlined, CopyOutlined
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import notificationMobileApi, { NotificationMobilePayload } from "../../services/notificationMobile";
import { addOneMinute } from "../../utils/TimeHelper";
import TimeZone from "../../services/timeZone";

const { Title } = Typography;

const getStatusTitle = (status_data: string | number) => {
  switch (status_data) {
    case "0":
    case 0:
      return "Draft Notifications";
    case "1":
    case 1:
      return "Active Notifications";
    case "2":
    case 2:
      return "Upcoming Notifications";
    case "3":
    case 3:
      return "Past Notifications";
    default:
      return "All Notifications";
  }
};

const NotificationMobileList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const status_data = searchParams.get("status") || "0";

  const [notifications, setNotifications] = useState<NotificationMobilePayload[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(6);
  const [messageApi, contextHolder] = message.useMessage();
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; id?: string }>({ open: false });
  const [cloneModal, setCloneModal] = useState<{ open: boolean; id?: string }>({ open: false });

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await notificationMobileApi.getNotificationsList(status_data);
      setNotifications(response.data || []);
    } catch (err) {
      console.error("Error fetching notifications:", err);
      messageApi.error("Failed to fetch notifications.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [status_data]);

  const handleDelete = async (id: string) => {
    try {
      await notificationMobileApi.deleteNotification(id);
      messageApi.success("Notification deleted successfully!");
      setPage(1);
      fetchNotifications();
    } catch (err: any) {
      console.error("Delete error:", err);
      messageApi.error(err.response?.data?.message || "Failed to delete notification.");
    } finally {
      setDeleteModal({ open: false });
    }
  };

  const handleEdit = (id: string) => {
    navigate(`/Notification-create-mobile/edit/${id}`);
  };

  const handleCloneConfirm = async (id: string, timeZone: any) => {
    try {
      setLoading(true);
      const payload = {
        id,
        timezone: TimeZone.getFriendlyTimeZone(),
      };

      await notificationMobileApi.cloneNotification(payload);
      messageApi.success("Notification cloned successfully in the drafts Section!");
      setTimeout(() => {
        navigate(`/Notification-mobile-list?status=0`);;
      }, 1000);
      setCloneModal({ open: false });

    } catch (err: any) {
      console.error("Clone error:", err);
      messageApi.error(err.response?.data?.message || "Failed to clone notification.");
    } finally {
      setLoading(false);
    }
  };


  const paginatedNotifications = notifications.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div>
      {contextHolder}

      {/* Delete confirmation modal */}
      <Modal
        title="Are you sure you want to delete this notification?"
        open={deleteModal.open}
        onOk={() => deleteModal.id && handleDelete(deleteModal.id)}
        onCancel={() => setDeleteModal({ open: false })}
        okText="Yes"
        cancelText="No"
        centered
      />

      {/* Clone Confirmation Modal */}
      <Modal
        open={cloneModal.open}
        footer={null}
        centered
        closable={false}
        bodyStyle={{
          textAlign: "center",
          padding: "24px 16px",
          borderRadius: 12,
        }}
      >
        <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
          Clone Notification
        </h3>
        <p style={{ fontSize: 14, color: "#555", marginBottom: 24 }}>
          Are you sure you want to clone this notification?
          A copy of the notification will be saved to the draft.
        </p>

        {/* Custom Buttons */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Button
            type="primary"
            block
            style={{
              backgroundColor: "#41787C",
              borderColor: "#41787C",
              height: 40,
              fontWeight: 500,
            }}
            onClick={() => {
              if (cloneModal.id) handleCloneConfirm(cloneModal.id);
              setCloneModal({ open: false });
            }}
          >
            Yes
          </Button>

          <Button
            block
            style={{
              backgroundColor: "#f2f2f2",
              border: "none",
              color: "#333",
              height: 40,
              fontWeight: 500,
            }}
            onClick={() => setCloneModal({ open: false })}
          >
            No
          </Button>
        </div>
      </Modal>

      {/* Page Title */}
      <Title level={3} style={{ color: "#41787C" }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined style={{ fontSize: 18, fontWeight: "bold", color: "#41787C", marginRight: 10 }} />}
          onClick={() => navigate(-1)}
          style={{ marginBottom: 10, padding: 0, color: "#41787C" }}
        />
        {getStatusTitle(status_data)}
      </Title>

      {/* Loading state */}
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "70vh" }}>
          <img src="/images/newLoading.gif" alt="Loading..." style={{ width: 200, height: 150 }} />
        </div>
      ) : (
        <>
          <Row gutter={[16, 16]}>
            {paginatedNotifications.length > 0 ? (
              paginatedNotifications.map((notification) => (
                <Col key={notification.id} xs={24} sm={12} md={8}>
                  <Card
                    hoverable
                    cover={
                      notification.announcementImageResponses?.length > 0 ? (
                        <img
                          alt={notification.title}
                          src={notification.announcementImageResponses[0].fileUrl}
                          style={{ height: 150, objectFit: "cover" }}
                        />
                      ) : (
                        <div
                          style={{
                            height: 150,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "#f0f0f0",
                            color: "#999",
                          }}
                        >
                          No Image
                        </div>
                      )
                    }
                  >
                    {/* Campaign Name and Action Buttons Row */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: 4,
                      }}
                    >
                      {/* Campaign Name */}
                      <div style={{ flex: 1, marginRight: 8 }}>
                        <Tooltip title={notification.campaignName}>
                          <Title
                            level={5}
                            style={{
                              margin: 0,
                              height: 20,
                              fontSize: 14,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                            }}
                          >
                            {notification.campaignName}
                          </Title>
                        </Tooltip>
                      </div>

                      {/* Action buttons */}
                      <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                        {notification.status === 3 ? (
                          // CLONE BUTTON
                          <Tooltip title="Clone">
                            <Button
                              type="text"
                              icon={<CopyOutlined style={{ color: "#3E767A" }} />}
                              onClick={(e) => {
                                e.stopPropagation();
                                setCloneModal({ open: true, id: notification.id }); //  open clone confirmation
                              }}
                            />
                          </Tooltip>
                        ) : (
                          // EDIT BUTTON
                          <Tooltip title="Edit">
                            <Button
                              type="text"
                              disabled={notification.status === 3}
                              icon={<EditOutlined style={{ color: "#3E767A" }} />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(notification.id); //  separate edit function
                              }}
                            />
                          </Tooltip>
                        )}

                        {/*DELETE BUTTON */}
                        <Tooltip title="Delete">
                          <Button
                            type="text"
                            disabled={notification.status === 1 || notification.status === 3}
                            icon={<DeleteOutlined style={{ color: "#3E767A" }} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteModal({ open: true, id: notification.id });
                            }}
                          />
                        </Tooltip>

                        {/* VIEW BUTTON */}
                        <Tooltip title="View">
                          <Button
                            type="text"
                            icon={<EyeOutlined style={{ color: "#3E767A" }} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              const viewPath =
                                notification.status === 1 || notification.status === 3
                                  ? `/Notification-create-mobile/views/${notification.id}`
                                  : `/Notification-create-mobile/view/${notification.id}`;
                              navigate(viewPath);
                            }}
                          />
                        </Tooltip>
                      </div>

                    </div>

                    {/* Title Row */}
                    <div style={{ marginBottom: 8 }}>
                      <Tooltip title={notification.title}>
                        <Title
                          level={5}
                          style={{
                            margin: 0,
                            height: 20,
                            fontSize: 13,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {notification.title}
                        </Title>
                      </Tooltip>
                    </div>


                    {/* Status */}
                    <Title level={5} style={{ margin: "8px 0 0", fontSize: 14, color: "#C19763" }}>
                      {notification.status === 1
                        ? `Active Till: ${notification.endDate
                          ? formatDate(notification.endDate)
                          : `${formatDate(notification.startDate)} ${addOneMinute(notification.startTime)}`
                        }`
                        : `Last Saved: ${formatDate(notification.createdAt)}`}
                    </Title>
                  </Card>
                </Col>
              ))
            ) : (
              <p>No notifications found.</p>
            )}
          </Row>

          {/* Pagination */}
          {notifications.length > 0 && (
            <div style={{ marginTop: 20, textAlign: "center" }}>
              <Pagination
                current={page}
                pageSize={pageSize}
                total={notifications.length}
                onChange={(newPage, newPageSize) => {
                  setPage(newPage);
                  if (pageSize !== newPageSize) {
                    setPageSize(newPageSize);
                    setPage(1);
                  }
                }}
                showSizeChanger
                pageSizeOptions={["3", "6", "9", "15"]}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default NotificationMobileList;

